import { faChartGantt, faDownload, faFileExport, faImages } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Card, CardContent, CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { FC, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../contexts/auth/AuthContext";
import MapContext, { MapFilter, MapFilterPeriodEnum, MapFilterSubmitter } from "../../contexts/data/MapContext";
import UserGroupsContext from "../../contexts/data/UserGroupsContext";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import { CommunityGroup, GetUserGroupsConfigResponse, NewGetSiteEventCollectionResponse, SiteEventType, Species, User } from "../../api/dtos/dtos";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { AppSettings } from "../../AppSettings";
import isCurrentGroupAdmin from "../../functions/user";

interface SummaryCardProps {
    siteEventsSighting: NewGetSiteEventCollectionResponse | undefined
    siteEventsSightingLoading: boolean
    siteEventsDamage: NewGetSiteEventCollectionResponse | undefined
    siteEventsDamageLoading: boolean
    siteEventsControl: NewGetSiteEventCollectionResponse | undefined
    siteEventsControlLoading: boolean
    siteEventsDisease: NewGetSiteEventCollectionResponse | undefined
    siteEventsDiseaseLoading: boolean
}

const SummaryCard: FC<SummaryCardProps> = ({
    siteEventsSighting,
    siteEventsSightingLoading,
    siteEventsDamage,
    siteEventsDamageLoading,
    siteEventsControl,
    siteEventsControlLoading,
    siteEventsDisease,
    siteEventsDiseaseLoading
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { filter } = useContext(MapContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { species: {species} } = useContext(SpeciesContext);

    //
    // -- Params --
    const { species: speciesName } = useParams();

    //
    // -- States --
    const [hasGalleryAccess, setHasGalleryAccess] = useState<boolean>(false);

    const hasDownloadRights = (userGroupsConfig: GetUserGroupsConfigResponse | undefined, user: User | null) => {
        if (userGroupsConfig && userGroupsConfig.communityGroups){
            let currentGroup = userGroupsConfig.communityGroups.find(cg => cg.communityGroupId === filter.bySubmitter.communityGroupId);
            let communityGroupMember = currentGroup?.communityGroupMembers?.find(x => x.userId == user?.id);

            if (communityGroupMember?.downloadRights) {
                return true;
            }
        }
        return false;
    }

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccess = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return user?.matchingRegionAdminRoles != undefined && user?.matchingRegionAdminRoles?.length > 0;
    }, [user]);

    const canDownload = useMemo(() => {
        if (isAdminOrSuperAdmin) {
            return true;
        }
        if (isDataAccessForThisSpecies) {
            return true;
        }
        if (isGroupAdmin) {
            return true;
        }
        if (hasDownloadRights(userGroupsConfig, user)) {
            return true;
        }
        if (user && filter?.bySubmitter?.submitter == MapFilterSubmitter.MyData) {
            return true;
        }
        return false;
    }, [isAdminOrSuperAdmin, isDataAccessForThisSpecies, isGroupAdmin, userGroupsConfig, user, filter]);
    
    useEffect(() => {
        if ((filter.bySubmitter.submitter === MapFilterSubmitter.MyData || filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup) || 
        (isDataAccess && isDataAccessForThisSpecies)) {
            setHasGalleryAccess(true);
        }
        else {
            setHasGalleryAccess(false);
        }
    }, [setHasGalleryAccess, user, filter, isDataAccess, isDataAccessForThisSpecies]);
    

    const getSummaryTitle = (filter: MapFilter) => {
        let title = "";
        if (filter.bySubmitter.submitter === MapFilterSubmitter.AllData) {
            if (user?.roles?.includes("dataaccess")){
                title = "Region Records - ";
            }
            else {
                title = "All Records - ";
            }
        }
        else if (filter.bySubmitter.submitter === MapFilterSubmitter.MyData) {
            title = "My Records - ";
        }
        else if (filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup) {
            title = "Group Records - ";
        }

        if (species === Species.WildDog || species === Species.Deer || species === Species.Fox) {
            title += "Export records, or view charts and tables";
        }
        else {
            title += "Export records";
        }

        return title;
    }

    const getSummarySubTitle = (filter: MapFilter) => {
        let subTitle = "Data Summary for ";
        
        // Period from filter
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        subTitle += `${dayjs(from).format('DD/MM/YYYY')} to ${dayjs(to).format('DD/MM/YYYY')} `;

        // Submitter label
        let submitterLabel = undefined;
        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                submitterLabel = "All Data";
                break;
            case MapFilterSubmitter.PublicData:
                submitterLabel = "Public Data";
                break;
            case MapFilterSubmitter.MyData:
                submitterLabel = "My Data";
                break;
            case MapFilterSubmitter.MyGroup:
                submitterLabel = userGroupsConfig?.communityGroups?.find((x: CommunityGroup) => x.communityGroupId === filter.bySubmitter.communityGroupId)?.name ?? "";
                break;
        }

        subTitle += submitterLabel;

        return subTitle;
    }

    // Generates the export links for the download buttons
    const generateExportLink = (speciesname: string, siteEventType: SiteEventType, filter: MapFilter, user: User | null) => {
        let siteEventTypeString = "";
        switch (siteEventType){
            case SiteEventType.Sighting:
                siteEventTypeString += "sighting";
                break;
            case SiteEventType.Damage:
                siteEventTypeString += "damage";
                break;
            case SiteEventType.Control:
                siteEventTypeString += "control";
                break;
            case SiteEventType.Disease:
                siteEventTypeString += "disease";
                break;
            case SiteEventType.Management:
                siteEventTypeString += "management";
                break;
        }

        // Period from filter
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
            from = filter.reportingPeriod.fromField;
            to = filter.reportingPeriod.toField;
            break;
            
            case MapFilterPeriodEnum.Year: 
            const year = filter.reportingPeriod.yearField;
            from = year?.startOf('year');
            
            // If the year is equal to this year, we want to cap off
            // at the current date.
            if (year?.year() === dayjs().year()) {
                to = dayjs();
            } else {
                to = year?.endOf('year');
            }
            
            break;
            
            case MapFilterPeriodEnum.MonthYear: 
            const monthYear = filter.reportingPeriod.monthYearField;
            from = monthYear?.startOf('month');
            
            // If the year is equal to this year, we want to cap off
            // at the current date.
            if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                to = dayjs();
            } else {
                to = monthYear?.endOf('month');
            }
            
            break;
            
            case MapFilterPeriodEnum.LastSevenDays: 
            from = dayjs().subtract(7, 'days');  
            to = dayjs();  
            break;
            
            case MapFilterPeriodEnum.LastThirtyOneDays: 
            from = dayjs().subtract(31, 'days');  
            to = dayjs();  
            break;
        }
        
        let link = `${AppSettings.baseUrl}${AppSettings.basePath}/siteevent${siteEventTypeString}${speciesname.replace("scan", "").replace("alert", "")}getcollectionexportcsv`;
        link += `?startDateOnOrAfter=${dayjs(from).format('YYYY-MM-DD')}&startDateOnOrBefore=${dayjs(to).format('YYYY-MM-DD')}`;
        
        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                link += "&take=100000";
                break;
            case MapFilterSubmitter.PublicData:
                link += "&onlyPublicData=true&take=100000";
                break;
            case MapFilterSubmitter.MyData:
                link += `&userid=${user?.id}&take=100000`;
                break;
            case MapFilterSubmitter.MyGroup:
                link += `&memberOfCommunityGroupIds=${filter.bySubmitter.communityGroupId}&take=100000`;
                if (filter.bySubmitter.communityGroupMemberId && filter.bySubmitter.communityGroupMemberId != -1) {
                    link += `&userId=${filter.bySubmitter.communityGroupMemberId}`;
                }
                break;
        }

        if (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) {
            link += `&dataAccessId=${filter.bySubmitter.dataAccessId}&locatedInRegionIds=${filter.bySubmitter.locatedInRegionIds}`;
        }
        return link;
    }

    return (
        <Card variant='outlined'>
            <CardContent>
                <Grid container spacing={1} alignItems={"center"}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faFileExport} style={{ fontSize: '1.2rem', paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24}>
                                {getSummaryTitle(filter)}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontSize={16}>
                            {getSummarySubTitle(filter)}
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{padding: "10px"}} />

                <Table>
                    <TableHead>
                        <TableRow sx={{backgroundColor: "#f0f0f0"}}>
                            <TableCell align="left">
                                <Typography fontSize={16} fontWeight={600}>
                                    Record Type
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography fontSize={16} fontWeight={600}>
                                    Count
                                </Typography>
                            </TableCell>
                            {canDownload && (
                                <TableCell align="left">
                                    <Typography fontSize={16} fontWeight={600}>
                                        Export
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filter.recordType.sighting && (
                            <>
                                {!siteEventsSightingLoading && siteEventsSighting && (
                                    <TableRow key="sighting">
                                        <TableCell align="left">Sighting</TableCell>
                                        <TableCell align="left">{siteEventsSighting.totalCount}</TableCell>
                                        {canDownload && (
                                            <TableCell align="left">
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                                                    href={generateExportLink(speciesName ?? "", SiteEventType.Sighting, filter, user)}
                                                >
                                                    Download
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </>
                        )}
                        {filter.recordType.damage && (
                            <>
                                {!siteEventsDamageLoading && siteEventsDamage && (
                                    <TableRow key="damage">
                                        <TableCell align="left">Damage</TableCell>
                                        <TableCell align="left">{siteEventsDamage.totalCount}</TableCell>
                                        {canDownload && (
                                            <TableCell align="left">
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                                                    href={generateExportLink(speciesName ?? "", SiteEventType.Damage, filter, user)} 
                                                >
                                                    Download
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </>
                        )}
                        {filter.recordType.control && (
                            <>
                                {!siteEventsControlLoading && siteEventsControl && (
                                    <TableRow key="control">
                                        <TableCell align="left">Control</TableCell>
                                        <TableCell align="left">{siteEventsControl.totalCount}</TableCell>
                                        {canDownload && (
                                            <TableCell align="left">
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                                                    href={generateExportLink(speciesName ?? "", SiteEventType.Control, filter, user)} 
                                                >
                                                    Download
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </>
                        )}
                        {filter.recordType.disease && (
                            <>
                                {!siteEventsDiseaseLoading && siteEventsDisease && (
                                    <TableRow key="disease">
                                        <TableCell align="left">Disease</TableCell>
                                        <TableCell align="left">{siteEventsDisease.totalCount}</TableCell>
                                        {canDownload && (
                                            <TableCell align="left">
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                                                    href={generateExportLink(speciesName ?? "", SiteEventType.Disease, filter, user)} 
                                                >
                                                    Download
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>

                { siteEventsSightingLoading || siteEventsDamageLoading || siteEventsControlLoading || siteEventsDiseaseLoading && (
                    <Box sx={{display: "flex", justifyContent: "center", width: "auto", paddingTop: "20px"}}>
                        <CircularProgress />
                    </Box>
                )}

                <Box sx={{padding: "10px"}} />

                <Button 
                    variant="text" 
                    startIcon={<FontAwesomeIcon icon={faChartGantt} />}
                    onClick={() => navigate("/" + speciesName + "/chartsandtables")}
                >
                    View Charts and Tables
                </Button>
                { hasGalleryAccess && (
                    <Button 
                        variant="text" 
                        startIcon={<FontAwesomeIcon icon={faImages} />}
                        onClick={() => navigate("/" + speciesName + "/photogallery")}
                    >
                        Photo Gallery
                    </Button>
                )}
            </CardContent>
        </Card>
    )
}

export default SummaryCard